import { PageHeader, Row, Skeleton } from '@leanin/ui';
import { Fragment, type ReactNode } from 'react';
import { DetailPage } from '../page-shell/index';
import { ProfileBodySkeleton } from './ProfileContentView';

type Props = {
  children?: ReactNode;
};

// Polished placeholder for ProfileChrome. Renders through the SAME shell
// (DetailPage with a tone-tinted cover) as the loaded ProfileChrome — so the
// skeleton can't drift from the live layout's banner height / card overlap.
// Only the slot contents differ: each PageHeader slot gets a Skeleton sized
// to match the real content's footprint.
//
// `subtitle` is string-locked on PageHeader, so the secondary-line
// placeholder rides the `preview` slot (ReactNode) — PageHeader renders it
// in the same vertical position the real subtitle would occupy, keeping the
// loading rhythm stable.
export function ProfileChromeSkeleton({ children }: Props) {
  const pageHeader = (
    <PageHeader
      leading={<Skeleton width={96} height={96} rounded="full" />}
      title={<Skeleton width={260} height={37} rounded="sm" />}
      preview={<Skeleton width={320} height={24} rounded="sm" />}
      meta={<FactoidSkeletons />}
      actions={
        <Fragment>
          <Skeleton width={108} height={36} rounded="md" />
          <Skeleton width={108} height={36} rounded="md" />
        </Fragment>
      }
    />
  );

  return (
    <DetailPage header={pageHeader} cover={{ tone: 'navy' }}>
      {children ?? <ProfileBodySkeleton />}
    </DetailPage>
  );
}

// Mirrors GroupHeaderFactoids' inner rhythm (icon + label, DotSep between).
// Two factoids matches ProfileChrome's typical "member since · location"
// loaded state. PageHeader's `meta` slot already wraps content in
// <Row gap="md" align="center" wrap>, so we emit only the inner fragments.
function FactoidSkeletons() {
  return (
    <Fragment>
      <Row gap="xs" align="center">
        <Skeleton width={16} height={16} rounded="sm" />
        <Skeleton width={120} height={18} rounded="sm" />
      </Row>
      <Skeleton width={3} height={3} rounded="full" />
      <Row gap="xs" align="center">
        <Skeleton width={16} height={16} rounded="sm" />
        <Skeleton width={100} height={18} rounded="sm" />
      </Row>
    </Fragment>
  );
}
